/** Bounded requests; writes are retried only by the user with the same submission ID. */
export class ApiError extends Error {
  constructor(message: string, public status: number, public payload: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

export function businessApiBase(configured: string | undefined, supabaseUrl: string, production: boolean): string {
  const base = configured?.replace(/\/$/, "");
  // Also handles an old Cloudflare build setting pointing directly at Supabase.
  if (production && (!base || base === `${supabaseUrl.replace(/\/$/, "")}/functions/v1/api`)) return "/api";
  return base || `${supabaseUrl.replace(/\/$/, "")}/functions/v1/api`;
}

export async function requestJson<T>(
  url: string,
  getToken: () => Promise<string>,
  init: RequestInit = {},
  timeoutMs = 30_000,
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const aborted = () => controller.abort();
  init.signal?.addEventListener("abort", aborted, { once: true });
  if (init.signal?.aborted) controller.abort();
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
      reject(new ApiError("连接超时，请稍后重试。提交结果可能已保存，请继续本次提交或到「我的方案」查看。", 408, { code: "request_timeout" }));
    }, timeoutMs);
  });
  try {
    return await Promise.race([deadline, (async () => {
      const token = await getToken();
      if (!token) throw new ApiError("登录已过期，请重新登录后继续。", 401, {});
      if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");
      const headers = new Headers(init.headers);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      headers.set("Authorization", `Bearer ${token}`);
      const res = await fetch(url, { ...init, headers, signal: controller.signal });
      const raw = await res.text();
      let body: Record<string, unknown>;
      try { body = JSON.parse(raw); }
      catch { throw new ApiError("服务响应异常，请稍后重试。", res.ok ? 502 : res.status, {}); }
      if (!res.ok) {
        const message = res.status === 401 ? "登录已过期，请重新登录后继续。"
          : typeof body?.error === "string" ? body.error : `服务暂时不可用（${res.status}），请稍后重试。`;
        throw new ApiError(message, res.status, body);
      }
      if (!body || typeof body !== "object") throw new ApiError("服务响应异常，请稍后重试。", 502, {});
      return body as T;
    })()]);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (timedOut) throw new ApiError("连接超时，请继续本次提交或到「我的方案」查看。", 408, {});
    if (init.signal?.aborted) throw new ApiError("请求已取消。", 499, {});
    throw new ApiError("网络连接失败，请检查网络后重试。已填写资料会保留，提交结果可到「我的方案」查看。", 0, { code: "network_error" });
  } finally {
    clearTimeout(timer!);
    init.signal?.removeEventListener("abort", aborted);
  }
}
